import { useState } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { PLACE_TYPES, PLACE_TYPE_LABELS, FURNISHED, BATHROOM_TYPES } from '../../lib/listingConstants';

const StepPropertyType = ({ listing, patchDraft, onNext, saving }) => {
  const [placeType, setPlaceType] = useState(listing.place_type || '');
  const [furnished, setFurnished] = useState(listing.furnished || '');
  const [bedrooms, setBedrooms] = useState(listing.bedroom_count ?? '');
  const [bathrooms, setBathrooms] = useState(listing.bathroom_count ?? '');
  const [bathType, setBathType] = useState(listing.bathroom_type || '');
  const [sharedWith, setSharedWith] = useState(listing.shared_with_others ?? false);
  const [sharedCount, setSharedCount] = useState(listing.shared_with_count ?? '');
  const [sharedDesc, setSharedDesc] = useState(listing.shared_with_description || '');

  const canContinue = placeType && furnished && bedrooms !== '' && bathrooms !== '' && bathType;

  const save = async () => {
    await patchDraft({
      place_type: placeType,
      furnished,
      bedroom_count: Number(bedrooms),
      bathroom_count: Number(bathrooms),
      bathroom_type: bathType,
      shared_with_others: !!sharedWith,
      shared_with_count: sharedWith ? Number(sharedCount || 0) : 0,
      shared_with_description: sharedWith ? sharedDesc : '',
    });
    onNext();
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-heading font-extrabold text-slate-900">What kind of place is it?</h1>
        <p className="mt-1 text-sm text-slate-600">This helps students find the right match.</p>
      </div>

      <div>
        <Label className="text-sm font-semibold text-slate-900 mb-2 block">Place type</Label>
        <div className="grid grid-cols-2 gap-2">
          {PLACE_TYPES.map((pt) => (
            <button
              key={pt}
              type="button"
              onClick={() => setPlaceType(pt)}
              className={`text-left px-3 py-3 rounded-xl border text-sm transition-colors ${
                placeType === pt
                  ? 'border-amber-500 bg-amber-50 text-amber-900 font-semibold'
                  : 'border-slate-200 bg-white text-slate-700 hover:border-amber-300'
              }`}
              data-testid={`place-type-${pt}`}
            >
              {PLACE_TYPE_LABELS[pt]}
            </button>
          ))}
        </div>
      </div>

      <div>
        <Label className="text-sm font-semibold text-slate-900 mb-2 block">Furnished?</Label>
        <div className="grid grid-cols-3 gap-2">
          {FURNISHED.map((f) => (
            <button
              key={f.value}
              type="button"
              onClick={() => setFurnished(f.value)}
              className={`px-3 py-2 rounded-xl border text-xs font-medium transition-colors ${
                furnished === f.value ? 'border-amber-500 bg-amber-50 text-amber-900' : 'border-slate-200 bg-white text-slate-700'
              }`}
              data-testid={`furnished-${f.value}`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-sm font-semibold text-slate-900 mb-2 block">Bedrooms</Label>
          <Input
            type="number"
            min="0"
            value={bedrooms}
            onChange={(e) => setBedrooms(e.target.value)}
            className="h-12"
            data-testid="bedroom-count"
          />
        </div>
        <div>
          <Label className="text-sm font-semibold text-slate-900 mb-2 block">Bathrooms</Label>
          <Input
            type="number"
            min="0"
            step="0.5"
            value={bathrooms}
            onChange={(e) => setBathrooms(e.target.value)}
            className="h-12"
            data-testid="bathroom-count"
          />
        </div>
      </div>

      <div>
        <Label className="text-sm font-semibold text-slate-900 mb-2 block">Bathroom type</Label>
        <div className="grid grid-cols-2 gap-2">
          {BATHROOM_TYPES.map((b) => (
            <button
              key={b.value}
              type="button"
              onClick={() => setBathType(b.value)}
              className={`px-3 py-2 rounded-xl border text-sm font-medium transition-colors ${
                bathType === b.value ? 'border-amber-500 bg-amber-50 text-amber-900' : 'border-slate-200 bg-white text-slate-700'
              }`}
              data-testid={`bath-type-${b.value}`}
            >
              {b.label}
            </button>
          ))}
        </div>
      </div>

      <Card className="bg-white border-slate-200">
        <CardContent className="p-4 space-y-3">
          <label className="flex items-center gap-2 text-sm font-semibold text-slate-900">
            <input
              type="checkbox"
              checked={sharedWith}
              onChange={(e) => setSharedWith(e.target.checked)}
              className="h-4 w-4 rounded border-slate-300"
              data-testid="shared-with-others"
            />
            Other tenants live here
          </label>
          {sharedWith && (
            <div className="space-y-2">
              <Input
                type="number"
                min="0"
                placeholder="Number of other tenants"
                value={sharedCount}
                onChange={(e) => setSharedCount(e.target.value)}
                className="h-11"
                data-testid="shared-count"
              />
              <textarea
                placeholder="Describe who else lives here (e.g. '2 international students, both 21')"
                value={sharedDesc}
                onChange={(e) => setSharedDesc(e.target.value)}
                maxLength={500}
                rows={3}
                className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm resize-none"
                data-testid="shared-description"
              />
            </div>
          )}
        </CardContent>
      </Card>

      <Button
        className="w-full btn-primary h-12 text-base"
        disabled={!canContinue || saving}
        onClick={save}
        data-testid="step-property-continue"
      >
        Continue
      </Button>
    </div>
  );
};

export default StepPropertyType;
